import { ApiError, getMessage, request } from './client';
import { packageListSchema, packageSchema } from '../components/coupons/couponSchema';

/**
 * Admin coupon packages: one coupon per reward tier, with a draft / active / archived
 * status. Exactly one package is active: the one app users see. Always hits the real backend.
 * Envelope: { Success, Message, Data }. Types: see components/coupons/couponSchema.js.
 */

const unexpected = () => new ApiError('Unexpected response from the server.', 200, null);

function parsePackage(data) {
  const parsed = packageSchema.safeParse(data);
  if (!parsed.success) throw unexpected();
  return parsed.data;
}

/**
 * GET admin/coupons?page&limit&search&brand&status (active first, then drafts, then archived).
 * @returns {Promise<import('../components/coupons/couponSchema').PackageList>}
 */
export async function getCouponPackages({ page, limit = 20, search = '', brand = '', status = 'all' }, { signal } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit), status });
  if (search) qs.set('search', search);
  if (brand) qs.set('brand', brand);
  const res = await request(`admin/coupons?${qs}`, { signal });

  const envelope = packageListSchema.safeParse(res?.Data);
  if (!envelope.success) throw unexpected();
  const coupons = envelope.data.coupons.flatMap((raw, index) => {
    const parsed = packageSchema.safeParse(raw);
    if (parsed.success) return [parsed.data];
    console.warn(`[coupons] skipped malformed package at index ${index}`);
    return [];
  });
  return { ...envelope.data, coupons };
}

/** GET admin/coupons/:id. 404 if it doesn't exist. */
export async function getCouponPackage(id, { signal } = {}) {
  const res = await request(`admin/coupons/${encodeURIComponent(id)}`, { signal });
  return parsePackage(res?.Data);
}

/**
 * POST admin/coupons (status defaults to draft). Saving as active archives the previous active package.
 * @param {import('../components/coupons/couponSchema').PackageInput} body
 */
export async function createCouponPackage(body) {
  const res = await request('admin/coupons', { method: 'POST', body });
  return { message: getMessage(res), coupon: parsePackage(res?.Data) };
}

/**
 * PUT admin/coupons/:id: saves everything, so always send the full form (status is required).
 * 409 when trying to move the active package to draft/archived.
 */
export async function updateCouponPackage(id, body) {
  const res = await request(`admin/coupons/${encodeURIComponent(id)}`, { method: 'PUT', body });
  return { message: getMessage(res), coupon: parsePackage(res?.Data) };
}

/** DELETE admin/coupons/:id: drafts and archived packages only (409 for the active one). */
export async function deleteCouponPackage(id) {
  const res = await request(`admin/coupons/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return { message: getMessage(res), id: res?.Data?.id ?? id };
}

export const couponsApi = {
  getCouponPackages,
  getCouponPackage,
  createCouponPackage,
  updateCouponPackage,
  deleteCouponPackage,
};
